import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { ImentionableUser } from './MentionableUser/MentionableUser';
import MentionedUsers from './MentionedUsers/MentionedUsers';
import MentionableUsersList from './MentionableUsersList/MentionableUsersList';
import CommentImageUploadButton from './CommentImageUploadButton/CommentImageUploadButton';
import { isValidFile } from '../../../utils/validateFiles';
import { IResponsibleUserData } from '../../../types/responsibleUser.types';
import { IorganizationBusinessData } from 'types/bussines';
import './BordMentions.scss';

const BordMentionsContent = ({
  placeholder,
  style = {},
  userList = [],
  userListProps = {},
  updateCommentsCallback,
  updateMentionedUsersCallback,
  bordMentionsInputProps = {},
  resetCount = 0,
  forwardedRef,
  isLoading = false,
  setImageFile,
  responsibleUsers,
  organizationInfo
}: IBordMentionsProps & { forwardedRef: React.ForwardedRef<IBordMentionsRef> }) => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [unmentionedUsers, setUnmentionedUsers] = useState<ImentionableUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ImentionableUser[]>([]);
  const [mentionedUsers, setMentionedUsers] = useState<ImentionableUser[]>([]);
  const [search, setSearch] = useState<string>('');

  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const bordMentionsInputRef = useRef<HTMLTextAreaElement>(null);
  const bordMentionsRef = useRef<HTMLDivElement>(null);
  const imageModalRef = useRef<HTMLDivElement>(null);
  const hasMentionedUsers = mentionedUsers?.length > 0;

  useImperativeHandle(forwardedRef, () => ({
    handleFileChange: (file?: File) => {
      if (file && file.type.startsWith('image/')) {
        setImageFile?.(file);
        setImagePreview(URL.createObjectURL(file));
      }
    },
    handleRemoveImage: () => {
      setImageFile?.(undefined);
      setImagePreview(null);
    }
  }));

  const focusOnTextEnd = () => {
    if (!bordMentionsInputRef.current) return;
    bordMentionsInputRef.current.focus();
    const textarea = bordMentionsInputRef.current;
    textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
  };

  const handleBeforeInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const inputEvent = e as unknown as InputEvent;
    if (!showOptions && inputEvent.data === '@') {
      e.preventDefault();
      return setShowOptions(true);
    }
    if (!showOptions || !inputEvent.data) return;
    e.preventDefault();
    setSearch(search + inputEvent.data);
  };

  const handleSelectUser = (user: ImentionableUser) => {
    setMentionedUsers(prev => [...prev, user]);
    setUnmentionedUsers(prev => prev.filter(u => u.userId !== user.userId));
    setSearch('');
    focusOnTextEnd();
  };

  const handleRemoveUser = (user: ImentionableUser) => {
    setMentionedUsers(prev => prev.filter(u => u.userId !== user.userId));
    setUnmentionedUsers(prev => [...prev, user]);
    focusOnTextEnd();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!showOptions) return;
    if (e.key === 'Escape') {
      setShowOptions(false);
      setSearch('');
      focusOnTextEnd();
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      setSearch(search.slice(0, -1));
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showOptions) return setShowOptions(false);
    bordMentionsInputRef.current?.focus();
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (bordMentionsRef.current && !bordMentionsRef.current.contains(e.target as Node)) {
      setShowOptions(false);
      setSearch('');
    }
    if (!showUploadImageModal) return;
    const isClickInModal = imageModalRef.current?.contains(e.target as Node);
    if (!isClickInModal) setShowUploadImageModal(false);
  };

  // Filtra los usuarios no mencionados según el término de búsqueda y actualiza la lista de usuarios filtrados.
  const filterUsers = () => {
    const filteredUsers = unmentionedUsers.filter(user => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = user.email.toLowerCase();
      const trimmedSearch = search.trim().toLowerCase();
      return fullName.includes(trimmedSearch) || email.includes(trimmedSearch);
    });
    setFilteredUsers(filteredUsers);
  };

  const resetComponent = () => {
    if (!resetCount || !bordMentionsInputRef.current) return;
    bordMentionsInputRef.current.value = '';
    setMentionedUsers([]);
    setUnmentionedUsers(userList);
  };

  const updateMessage = () => updateCommentsCallback?.(bordMentionsInputRef.current?.value?.trim() || '');

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isLoading) return;
    event.preventDefault();
    const items = event?.clipboardData?.items;
    const copyText = event?.clipboardData.getData('text');
    if (copyText) {
      const textArea = event?.target as HTMLTextAreaElement;
      textArea.value += copyText;
      updateCommentsCallback?.(textArea?.value);
    }
    const imageItem = Array.from(items).find(item => item.type.indexOf('image') !== -1);
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    const isValid = isValidFile({
      file,
      formatErrorMessage: 'Solo puedes cargar imágenes en los comentarios',
      sizeErrorMessage: 'La imagen que intentas cargar pesa más de 30mb'
    });
    if (!isValid) return;
    setImageFile?.(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const isValid = isValidFile({
      file,
      formatErrorMessage: 'Solo puedes cargar imágenes en los comentarios',
      sizeErrorMessage: 'La imagen que intentas cargar pesa más de 30mb'
    });
    if (!isValid) return;
    setImageFile?.(file);
    setImagePreview(URL.createObjectURL(file));
    setShowUploadImageModal(false);
  };

  const handleRemoveImage = () => {
    if (isLoading) return;
    setImageFile?.(undefined);
    setImagePreview(null);
    setShowUploadImageModal(false);
  };

  const setReponsibleUsersAsMentionedUsers = () => {
    if (!responsibleUsers || !responsibleUsers?.length) return;
    const kamAssigned = organizationInfo?.assignedKam;
    const responsibleUsersAsMentionedUsers = responsibleUsers
      .map(user => ({
        userId: user?.user?.id || user?.userId,
        firstName: user.user?.firstName,
        lastName: user.user?.lastName,
        email: user.user?.email
      }))
      .filter(
        user => user?.userId && user?.firstName && user?.lastName && user?.email && user?.userId !== kamAssigned?.id
      ) as ImentionableUser[];

    setMentionedUsers(responsibleUsersAsMentionedUsers);
  };

  useEffect(() => {
    setUnmentionedUsers(userList);
  }, [userList.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterUsers();
  }, [search, unmentionedUsers?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUploadImageModal]);

  useEffect(() => {
    // Llama al callback para actualizar los usuarios mencionados cada vez que cambian.
    updateMentionedUsersCallback?.(mentionedUsers);
  }, [mentionedUsers?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    resetComponent();
  }, [resetCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={bordMentionsRef} className="bordMentions" style={style} onKeyDown={handleKeyDown} onClick={handleClick}>
      {showOptions && (
        <MentionableUsersList
          search={search}
          users={filteredUsers}
          hideOptionsCallback={() => setShowOptions(false)}
          handleSelectUser={handleSelectUser}
          {...userListProps}
        />
      )}

      <div className="bordMentionsInput" {...bordMentionsInputProps}>
        <div className="bordImageAndCommentInput">
          <CommentImageUploadButton
            showUploadImageModal={showUploadImageModal}
            setShowUploadImageModal={setShowUploadImageModal}
            handleFileChange={handleFileChange}
            imageModalRef={imageModalRef}
            imagePreview={imagePreview}
            handleRemoveImage={handleRemoveImage}
            isLoading={isLoading}
          />

          <textarea
            ref={bordMentionsInputRef}
            placeholder={placeholder}
            disabled={isLoading}
            className="bordMentionsInputText"
            onBeforeInput={handleBeforeInput}
            onChange={updateMessage}
            onPaste={handlePaste}
          />
        </div>

        {hasMentionedUsers && (
          <MentionedUsers
            mentionedUsers={mentionedUsers}
            handleRemoveUser={handleRemoveUser}
            isDisabled={isLoading}
            responsibleUsers={responsibleUsers}
          />
        )}
      </div>
    </div>
  );
};

const BordMentions = forwardRef<IBordMentionsRef, IBordMentionsProps>((props, ref) => (
  <BordMentionsContent {...props} forwardedRef={ref} />
));

export default BordMentions;

export interface IBordMentionsProps {
  placeholder: string;
  style?: React.CSSProperties;
  userList?: ImentionableUser[];
  userListProps?: React.HTMLAttributes<HTMLDivElement>;
  updateCommentsCallback?: (comments: string) => void;
  updateMentionedUsersCallback?: (mentionedUsers: ImentionableUser[]) => void;
  bordMentionsInputProps?: React.HTMLAttributes<HTMLDivElement>;
  resetCount?: number;
  isLoading?: boolean;
  setImageFile?: (file?: File) => void;
  responsibleUsers?: IResponsibleUserData[];
  organizationInfo?: IorganizationBusinessData;
}

export interface IBordMentionsRef {
  handleFileChange: (file?: File) => void;
  handleRemoveImage: () => void;
}
